// Reduce main value: subtract 22 while > 22, if 0 then 22
function reduceMain(val) {
  val = Math.abs(val);
  while (val > 22) {
    val -= 22;
  }
  return val === 0 ? 22 : val;
}

// Public helper — same 22-rule reduction, exported for consumers that
// need to reduce arbitrary numbers (e.g. Σ sums of two birth dates).
export function reduce22(val) {
  return reduceMain(val);
}

// Elementwise sum of two karma result objects. When `reduce=true`, applies
// the 22-rule to each entry; otherwise returns raw integer sums. Only keys
// present in BOTH inputs are summed; missing entries fall back to 0.
export function sumKarmaResults(r1, r2, { reduce = true } = {}) {
  if (!r1 || !r2) return null;
  const out = {};
  const keys = new Set([...Object.keys(r1), ...Object.keys(r2)]);
  for (const k of keys) {
    const a = typeof r1[k] === 'number' ? r1[k] : 0;
    const b = typeof r2[k] === 'number' ? r2[k] : 0;
    const raw = a + b;
    // Additional keys (A1, B1, ...) follow the digit-sum rule; main keys the 22-rule.
    out[k] = reduce ? (k.endsWith('1') ? reduceAdditional(raw) : reduceMain(raw)) : raw;
  }
  return out;
}

// Σ of the raw date parts of two birth dates: main (22-rule) and additional
// (digit-sum) variants — e.g. day 18 + day 28 → main 18+6=24→2, add 18+10=28→10.
function sumDateParts(d1, d2) {
  const out = {};
  for (const [k, a, b] of [['day', d1.day, d2.day], ['month', d1.month, d2.month], ['year', d1.year, d2.year]]) {
    if (typeof a !== 'number' || typeof b !== 'number') continue;
    out[k] = reduceMain(reduceMain(a) + reduceMain(b));
    out[`${k}_r`] = out[k];
    out[`${k}1`] = reduceAdditional(reduceAdditional(a) + reduceAdditional(b));
  }
  return out;
}

// Reduce additional value: sum digits while > 22, if 0 then 22
function reduceAdditional(val) {
  val = Math.abs(val);
  while (val > 22) {
    val = String(val).split('').reduce((sum, d) => sum + parseInt(d, 10), 0);
  }
  return val === 0 ? 22 : val;
}

// Sum digits of a number
function digitSum(num) {
  return String(Math.abs(num)).split('').reduce((sum, d) => sum + parseInt(d, 10), 0);
}

export function calculateKarma(day, month, year) {
  // Base raw values
  const A_raw = day;
  const B_raw = month;
  const V_raw = digitSum(year);

  // A (day)
  const A = reduceMain(A_raw);
  const A1 = reduceAdditional(A_raw);

  // Б (month)
  const B = reduceMain(B_raw);
  const B1 = reduceAdditional(B_raw);

  // В (year digit sum)
  const V = reduceMain(V_raw);
  const V1 = reduceAdditional(V_raw);

  // Г = A + Б + В
  const G = reduceMain(A + B + V);
  const G1 = reduceAdditional(A1 + B1 + V1);

  // Д = A + Б
  const D = reduceMain(A + B);
  const D1 = reduceAdditional(A1 + B1);

  // Е = A + В
  const E = reduceMain(A + V);
  const E1 = reduceAdditional(A1 + V1);

  // Ж = Д + Е
  const Zh = reduceMain(D + E);
  const Zh1 = reduceAdditional(D1 + E1);

  // З = Б + В
  const Z = reduceMain(B + V);
  const Z1 = reduceAdditional(B1 + V1);

  // И = |A - Б|
  const I = reduceMain(A - B);
  const I1 = reduceAdditional(A1 - B1);

  // К = |A - В|
  const K = reduceMain(A - V);
  const K1 = reduceAdditional(A1 - V1);

  // Л = |И - К|
  const L = reduceMain(I - K);
  const L1 = reduceAdditional(I1 - K1);

  // М = |Б - В|
  const M = reduceMain(B - V);
  const M1 = reduceAdditional(B1 - V1);

  // HRT = sum of all digits of DD.MM.YYYY
  const dateStr = `${String(day).padStart(2, '0')}${String(month).padStart(2, '0')}${String(year)}`;
  const HRT_raw = dateStr.split('').reduce((s, d) => s + parseInt(d, 10), 0);
  const HRT = reduceMain(HRT_raw);
  const HRT1 = reduceAdditional(HRT_raw);

  // Manipura = A + Б + В + Г (using main values for main, additional for add)
  const Man = reduceMain(A + B + V + G);
  const Man1 = reduceAdditional(A1 + B1 + V1 + G1);

  return {
    A, A1, B, B1, V, V1, G, G1,
    D, D1, E, E1, Zh, Zh1, Z, Z1,
    I, I1, K, K1, L, L1, M, M1,
    HRT, HRT1, Man, Man1
  };
}

// Reduce to a single digit (0-9) by repeatedly summing digits.
// 0 stays 0 here (no 22-rule); used for gate calculation.
function reduceToSingleDigit(val) {
  val = Math.abs(val);
  while (val > 9) {
    val = String(val).split('').reduce((s, d) => s + parseInt(d, 10), 0);
  }
  return val;
}

// Calculate the 4 gate values from a birth date.
// Gate 1 = 36 - reduceToSingleDigit(sum of all digits of DD.MM.YYYY)
// Gate 2 = Gate 1 + 9
// Gate 3 = Gate 1 + 18
// Gate 4 = Gate 1 + 18  (same as Gate 3 — represents "X+" age range, no further +9)
export function calculateGates(day, month, year) {
  if (!day || !month || !year) return null;
  const dateStr = `${String(day).padStart(2, '0')}${String(month).padStart(2, '0')}${String(year)}`;
  const sumAll = dateStr.split('').reduce((s, d) => s + parseInt(d, 10), 0);
  const reduced = reduceToSingleDigit(sumAll);
  const g1 = 36 - reduced;
  return [g1, g1 + 9, g1 + 18, g1 + 18];
}

// Safe formula evaluator for custom table rows.
// Allows references to result keys (A, A1, B, B1, V, V1, ..., HRT, HRT1, etc.),
// numeric literals, parens, +, -, *, /, abs(), and reduces final result via reduceMain.
// Optional `pair` results — when provided, tokens `d1_X` resolve to `pair.date1[X]`
// and `d2_X` resolve to `pair.date2[X]`. Falls back to the base `results` for
// bare keys as before.
export function evaluateFormula(formula, results, { reduce = true, pair = null } = {}) {
  if (!formula || !results) return null;
  let expr = String(formula);
  // First substitute d1_XXX / d2_XXX prefixed tokens (longest first) if pair provided.
  if (pair && typeof pair === 'object') {
    for (const side of ['date1', 'date2']) {
      const prefix = side === 'date1' ? 'd1_' : 'd2_';
      const src = pair[side];
      if (!src) continue;
      const skeys = Object.keys(src).sort((a, b) => b.length - a.length);
      for (const k of skeys) {
        const val = src[k];
        if (typeof val !== 'number') continue;
        expr = expr.split(`${prefix}${k}`).join(`(${val})`);
      }
    }
  }
  // Match longest keys first (A1 before A, Zh1 before Zh, etc.)
  const keys = Object.keys(results).sort((a, b) => b.length - a.length);
  for (const k of keys) {
    const val = results[k];
    if (typeof val !== 'number') continue;
    expr = expr.split(k).join(`(${val})`);
  }
  // Whitelist characters: digits, operators, parens, dots, spaces, and "abs"
  const stripped = expr.replace(/abs/gi, '');
  if (!/^[\d+\-*/().\s]*$/.test(stripped)) return null;
  try {
    // eslint-disable-next-line no-new-func
    const result = new Function('abs', `"use strict"; return (${expr});`)(Math.abs);
    if (typeof result !== 'number' || !isFinite(result)) return null;
    const rounded = Math.round(result);
    return reduce ? reduceMain(rounded) : rounded;
  } catch {
    return null;
  }
}

// Evaluate a formula in DUAL mode — uses base keys (A, B, V, Man...) and
// substitutes BOTH .main and .add (A1, B1, V1, Man1...) variants. Returns
// {main, add} where each value is reduceMain-reduced (22-rule applied).
// Example: formula="Man+V", results={Man:18, Man1:8, V:2, V1:2}
//   main = reduceMain(18+2) = 20
//   add  = reduceMain(8+2)  = 10
export function evaluateFormulaPair(formula, results) {
  if (!formula || !results) return null;
  // Base keys are the ones WITHOUT a trailing "1"
  const baseKeys = Object.keys(results)
    .filter(k => !k.endsWith('1') || !results[k.slice(0, -1)])
    // keep only the actual "base" form — A, B, V, ..., HRT, Man
    .filter(k => Object.prototype.hasOwnProperty.call(results, `${k}1`))
    .sort((a, b) => b.length - a.length);

  const buildExpr = (useAdd) => {
    let expr = String(formula);
    for (const k of baseKeys) {
      const subKey = useAdd ? `${k}1` : k;
      const val = results[subKey];
      if (typeof val !== 'number') continue;
      // Replace whole-word base key with the chosen value
      expr = expr.split(k).join(`(${val})`);
    }
    return expr;
  };

  const evalSide = (useAdd) => {
    const expr = buildExpr(useAdd);
    const stripped = expr.replace(/abs/gi, '');
    if (!/^[\d+\-*/().\s]*$/.test(stripped)) return null;
    try {
      // eslint-disable-next-line no-new-func
      const result = new Function('abs', `"use strict"; return (${expr});`)(Math.abs);
      if (typeof result !== 'number' || !isFinite(result)) return null;
      const raw = Math.abs(Math.round(result));
      // main → 22-rule (subtract 22 while > 22)
      // add  → digit-sum rule (sum digits while > 22)
      return useAdd ? reduceAdditional(raw) : reduceMain(raw);
    } catch {
      return null;
    }
  };

  const main = evalSide(false);
  const add = evalSide(true);
  if (main === null && add === null) return null;
  return { main, add };
}

// ============================================================
// Grid Cells — evaluator for admin-defined draggable square cells.
// Cells can reference:
//   - sphere result keys (A, A1, B, B1, ..., HRT, HRT1, Man, Man1)
//   - date raw values: day, month, year
//   - reduced date values (22-rule): day_r, month_r, year_r
//   - other cells:  cell_<id>
// Supports +, -, *, /, parens, abs(). Final value reduceMain-reduced.
// ============================================================
export function buildGridContext(results, day, month, year, extras = null) {
  const ctx = { ...(results || {}) };
  if (typeof day === 'number') {
    ctx.day = day;
    ctx.day_r = reduceMain(day);
  }
  if (typeof month === 'number') {
    ctx.month = month;
    ctx.month_r = reduceMain(month);
  }
  if (typeof year === 'number') {
    ctx.year = year;
    ctx.year_r = reduceMain(year);
  }
  // Optional dual-date tokens for "İl Enerji Uyumu" mode.
  // extras = { results2, day2, month2, year2, resultsSum }
  if (extras && typeof extras === 'object') {
    const r2 = extras.results2 || null;
    if (r2) {
      for (const [k, v] of Object.entries(r2)) {
        if (typeof v === 'number') ctx[`d2_${k}`] = v;
      }
    }
    if (results) {
      for (const [k, v] of Object.entries(results)) {
        if (typeof v === 'number') ctx[`d1_${k}`] = v;
      }
    }
    if (typeof extras.day === 'number') ctx.d1_day = extras.day;
    if (typeof extras.month === 'number') ctx.d1_month = extras.month;
    if (typeof extras.year === 'number') ctx.d1_year = extras.year;
    if (typeof extras.day2 === 'number') ctx.d2_day = extras.day2;
    if (typeof extras.month2 === 'number') ctx.d2_month = extras.month2;
    if (typeof extras.year2 === 'number') ctx.d2_year = extras.year2;
    // Σ values: use the page-provided map, else compute from both results.
    const sum = extras.resultsSum || (r2 && results ? sumKarmaResults(results, r2) : null);
    if (sum) {
      for (const [k, v] of Object.entries(sum)) {
        if (typeof v === 'number') ctx[`sum_${k}`] = v;
      }
    }
    if (r2) {
      const parts = sumDateParts(
        { day: extras.day ?? day, month: extras.month ?? month, year: extras.year ?? year },
        { day: extras.day2, month: extras.month2, year: extras.year2 },
      );
      for (const [k, v] of Object.entries(parts)) ctx[`sum_${k}`] = v;
    }
  }
  return ctx;
}

// Evaluate `formula` against `subs` (longest key first). Returns a rounded
// number or null when the formula is invalid.
function evalExpr(formula, subs) {
  const keys = Object.keys(subs).sort((a, b) => b.length - a.length);
  let expr = String(formula);
  for (const k of keys) {
    const val = subs[k];
    if (typeof val !== 'number') continue;
    expr = expr.split(k).join(`(${val})`);
  }
  const stripped = expr.replace(/abs/gi, '');
  if (!/^[\d+\-*/().\s]*$/.test(stripped)) return null;
  try {
    // eslint-disable-next-line no-new-func
    const result = new Function('abs', `"use strict"; return (${expr});`)(Math.abs);
    if (typeof result !== 'number' || !isFinite(result)) return null;
    return Math.round(result);
  } catch {
    return null;
  }
}

// Evaluate a single cell. `cellResults` is { cellId: number } map of
// already-computed cells (for cross-references).
// Returns `{ v1, v2 }` where either may be null if not configured / unresolvable.
export function evaluateGridCell(cell, ctx, cellResults = {}) {
  if (!cell) return { v1: null, v2: null };
  if (cell.enabled === false) return { v1: null, v2: null };

  // date_source='sum' overlays sum_XXX values onto the base names so a
  // formula like `HRT+A` resolves to `sum_HRT + sum_A`. Regular d1_/d2_
  // tokens still work because they were injected by buildGridContext.
  let effectiveCtx = ctx;
  const isSum = cell.date_source === 'sum';
  if (isSum) {
    effectiveCtx = { ...ctx };
    for (const k of Object.keys(ctx)) {
      if (k.startsWith('sum_')) {
        effectiveCtx[k.slice(4)] = ctx[k];
      }
    }
  }

  const cellSubs = () => {
    const subs = {};
    for (const [cid, val] of Object.entries(cellResults)) {
      const main = val && typeof val === 'object' ? val.v1 : val;
      if (typeof main === 'number') subs[`cell_${cid}`] = main;
    }
    return subs;
  };

  const resolveOne = (source, autoField, formula) => {
    if (source === 'none') return null;
    if (source === 'auto') {
      const v = effectiveCtx[autoField];
      return typeof v === 'number' ? v : null;
    }
    const f = String(formula || '').trim();
    if (!f) return null;
    const r = evalExpr(f, { ...effectiveCtx, ...cellSubs() });
    return r === null ? null : reduceMain(r);
  };

  // Σ mode with no explicit 2nd source: derive the additional value with the
  // digit-sum rule by swapping every base key (A, day, ...) for its X1 twin.
  const resolveSumAdditional = (source, autoField, formula) => {
    if (source === 'none') return null;
    if (source === 'auto') {
      const key = autoField.endsWith('1') ? autoField : `${autoField}1`;
      const v = effectiveCtx[key] ?? effectiveCtx[`${autoField.replace(/_r$/, '')}1`];
      return typeof v === 'number' ? v : null;
    }
    const f = String(formula || '').trim();
    if (!f) return null;
    const subs = {};
    for (const [k, v] of Object.entries(effectiveCtx)) {
      const twin = effectiveCtx[`${k.replace(/_r$/, '')}1`];
      subs[k] = typeof twin === 'number' ? twin : v;
    }
    const r = evalExpr(f, { ...subs, ...cellSubs() });
    return r === null ? null : reduceAdditional(Math.abs(r));
  };

  const v1 = resolveOne(cell.source, cell.auto_field, cell.formula);
  const source2 = cell.source2 || 'none';
  const v2 = isSum && source2 === 'none'
    ? resolveSumAdditional(cell.source, cell.auto_field, cell.formula)
    : resolveOne(source2, cell.auto_field2, cell.formula2);
  return { v1, v2 };
}

// Compute all grid cell values. Performs up to N passes so cells can
// reference other cells (max chain depth = cells.length).
// Returns { [id]: { v1, v2 } }.
// `extras` (optional) enables dual-date tokens (d1_XXX, d2_XXX, sum_XXX)
// by forwarding results2/day2/month2/year2/resultsSum into buildGridContext.
export function computeGridCellValues(cells, results, day, month, year, extras = null) {
  if (!Array.isArray(cells) || cells.length === 0) return {};
  const ctx = buildGridContext(results, day, month, year, extras);
  const cellResults = {};
  const maxPasses = cells.length + 1;
  let lastSolved = -1;
  for (let pass = 0; pass < maxPasses; pass++) {
    let solvedThisPass = 0;
    for (const cell of cells) {
      if (cellResults[cell.id] !== undefined) continue;
      const r = evaluateGridCell(cell, ctx, cellResults);
      if (r && (r.v1 !== null || r.v2 !== null)) {
        cellResults[cell.id] = r;
        solvedThisPass++;
      }
    }
    if (solvedThisPass === 0) break;
    if (lastSolved === Object.keys(cellResults).length) break;
    lastSolved = Object.keys(cellResults).length;
  }
  return cellResults;
}
